import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import EditorIcons from './EditorIcons';
import { EDITOR_IGNORED_WORDS } from './constants';
import { getConfig } from '../../selectors/config';
// Side-effect import: registers the `languagetool` plugin on the global
// `tinymce` object that the self-hosted bundle loaded by public/index.html
// exposes.
import './plugins/languagetool';

// Squiggle styles for LanguageTool annotations (red = spelling, blue = grammar).
const languageToolContentStyle = [
  '.lt-error { cursor: pointer; background-size: 4px 2px; background-repeat: repeat-x; background-position: left bottom; }',
  '.lt-error.lt-spelling { background-image: linear-gradient(to right, transparent 0, transparent 25%, #d32f2f 25%, #d32f2f 50%, transparent 50%, transparent 75%, #d32f2f 75%, #d32f2f 100%); }',
  '.lt-error.lt-grammar { background-image: linear-gradient(to right, transparent 0, transparent 25%, #1976d2 25%, #1976d2 50%, transparent 50%, transparent 75%, #1976d2 75%, #1976d2 100%); }'
].join(' ');

const TextEditor = ({
  onBlur,
  onChange,
  initialEditorContent,
  icons = [],
  onSetupActions,
  maxAllowedCharacters,
  height = 500,
  hideToolbar,
  placeholder,
}) => {
  const editorRef = useRef(null);
  let currentEditorContent = initialEditorContent;

  // editor-languagetool-spellcheck gates the LanguageTool restore (JPD-177).
  // OFF is the kill-switch: the plugin is still registered on the global tinymce
  // by the side-effect import above, but it is not added to this editor's plugin
  // list or toolbar, so the editor behaves exactly as it does today.
  // useFlags() can be undefined before the LaunchDarkly client initializes, so
  // default to {} to avoid a destructure throw (matches usage elsewhere, e.g. ServiceManager).
  const { editorLanguagetoolSpellcheck } = useFlags() ?? {};
  const { LANGUAGETOOL_URL } = useSelector(getConfig);

  const setup = (editor) => {
    // Adding icons - load defaults from EditorIcons.js then icons from props
    for (const item of EditorIcons) {
      editor.ui.registry.addIcon(item.name, item.icon);
    }
    for (const item of [...icons, ...(onSetupActions?.icons ?? [])]) {
      editor.ui.registry.addIcon(item.name, item.icon);
    }

    editor.ui.registry.addMenuButton('sizeandcolor', {
      icon: 'change-case',
      fetch: (callback) => {
        const items = 'blockformats fontsizes forecolor backcolor';
        callback(items);
      }
    });

    // Adding toggle buttons
    if (onSetupActions?.toggleButtons) {
      for (const item of onSetupActions.toggleButtons) {
        editor.ui.registry.addToggleButton(item.name, {
          icon: item.icon,
          tooltip: item.tooltip,
          onAction: item.onAction
        });
      }
    }
  };

  const onChangeEvent = () => {
    const content = editorRef.current.getContent();
    if (maxAllowedCharacters && content.length > maxAllowedCharacters) {
      alert(`Description is too long. The character limit is ${maxAllowedCharacters}. Sorry for the inconvenience.`);
      editorRef.current.setContent(currentEditorContent);
    } else {
      currentEditorContent = content;
      onChange(content);
    }
  }

  const toolbar = hideToolbar
    ? false
    : `typeStuff sizeandcolor | bullist numlist | aligntext | table | ${editorLanguagetoolSpellcheck ? 'languagetool | ' : ''}remainder | undo redo`;

  const languageToolInit = editorLanguagetoolSpellcheck
    ? {
        languagetool_url: LANGUAGETOOL_URL,
        languagetool_language: 'en-US',
        // Domain dictionary (TP abbreviations, entity suffixes) restored from
        // the retired TinyMCE premium spellchecker so LanguageTool stops
        // flagging NCP / OECD / TNMM / interquartile etc. as misspellings.
        languagetool_ignore_list: EDITOR_IGNORED_WORDS,
        // Stays false (TinyMCE default): Chrome/Edge enhanced spellcheck ships
        // editor contents to Google/Microsoft (spell-jacking); LanguageTool
        // keeps the data in-VPC.
        browser_spellcheck: false,
      }
    : {};

  return (
    <Editor
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      onInit={function (evt, editor) {
        editorRef.current = editor;
        editor.on('keyup', function () {
          onChangeEvent();
        })
        editor.on('paste', function () {
          onChangeEvent();
        })
        editor.on('cut', function () {
          onChangeEvent();
        })
        editor.on('blur', function () {
          if (onBlur) {
            onBlur();
          } else {
            onChangeEvent();
          }
        })
      }}
      initialValue={initialEditorContent}
      placeholder={placeholder}
      init={{
        selector: 'textarea',
        height,
        menubar: false,
        plugins: [
          'lists link image charmap print preview anchor',
          'searchreplace visualblocks fullscreen',
          'insertdatetime media table paste wordcount',
          ...(editorLanguagetoolSpellcheck ? ['languagetool'] : []),
        ],
        toolbar,
        toolbar_groups: {
          aligntext: {
            icon: 'align-left',
            tooltip: 'alignment',
            items: 'alignleft aligncenter alignright alignjustify'
          },
          typeStuff: {
            icon: 'format-icon',
            tooltip: 'formatting',
            items: 'bold italic underline strikethrough | subscript superscript'
          },
          remainder: {
            icon: 'more-drawer',
            tooltip: 'Additional Options',
            items: 'outdent indent link removeformat'
          }
        },
        setup,
        content_style: `body { font-family: Helvetica, Arial, sans-serif; font-size: 13px; }${
          editorLanguagetoolSpellcheck ? ` ${languageToolContentStyle}` : ''
        }`,
        fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
        branding: false,
        statusbar: false,
        indentation: '20pt',
        ...languageToolInit,
      }}
    />
  );
}

export default TextEditor;